import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { ChevronLeft, ChevronRight, Moon, Plus, Trash2, Lock, Cloud } from 'lucide-react'
import { toast } from 'sonner'
import { useTracker } from '@/tracker/store'
import { useSync } from '@/tracker/sync'
import { computeForecast, classifyDay, todayIso, phaseLabel } from '@/tracker/predict'
import type { DayPhase } from '@/tracker/predict'
import { usePageMeta } from '@/lib/use-document-meta'
import { MedicalDisclaimer } from '@/components/MedicalDisclaimer'
import { ThemeToggle } from '@/components/ThemeToggle'
import { Field, inputClass, primaryButtonClass, secondaryButtonClass } from '@/components/ui'

export const Route = createFileRoute('/tracker')({ component: TrackerPage })

const SYMPTOM_TYPES = [
  'Cramps',
  'Headache',
  'Mood',
  'Fatigue',
  'Breast tenderness',
  'Bloating',
  'Acne',
  'Sleep',
  'Other',
]

const phaseClass: Record<DayPhase, string> = {
  period: 'bg-flow-heavy text-white',
  'predicted-period': 'bg-flow-light text-text-primary',
  ovulation: 'bg-fertile text-white',
  fertile: 'bg-fertile/30 text-text-primary',
  normal: 'bg-surface-1 text-text-secondary',
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]
const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

function TrackerPage() {
  usePageMeta({
    path: '/tracker',
    title: 'Period Tracker — Lunara',
    description:
      'Track your period and symptoms privately in your browser — no account required. Or sign in to sync securely across your devices.',
    noindex: true,
  })

  const {
    cycles,
    symptoms,
    settings,
    loaded,
    load,
    addCycle,
    removeCycle,
    addSymptom,
    removeSymptom,
    updateSettings,
    clearAll,
  } = useTracker()
  const syncStatus = useSync((s) => s.status)
  const syncing = syncStatus === 'synced' || syncStatus === 'syncing' || syncStatus === 'pending'

  useEffect(() => {
    load().catch(() => toast.error('Could not open local storage'))
  }, [load])

  const today = todayIso()
  const forecast = useMemo(
    () => computeForecast(cycles, settings, today),
    [cycles, settings, today],
  )
  const periodStartDates = useMemo(() => cycles.map((c) => c.startDate), [cycles])

  return (
    <div className="bg-surface-0 min-h-screen">
      {/* Header */}
      <header className="border-border bg-surface-0/80 border-b backdrop-blur">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4">
          <Link to="/" className="text-text-primary flex items-center gap-2 font-semibold">
            <span className="bg-primary text-on-primary inline-flex h-7 w-7 items-center justify-center rounded-full">
              <Moon size={16} />
            </span>
            Lunara
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="text-text-secondary hover:bg-surface-2 rounded-lg px-3 py-2 text-sm font-medium"
            >
              Home
            </Link>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8">
        <div className="border-border bg-surface-1 text-text-secondary mb-6 flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm">
          {syncing ? (
            <>
              <Cloud size={16} className="text-success" />
              Synced to your account — end-to-end encrypted.
            </>
          ) : (
            <>
              <Lock size={16} className="text-primary" />
              Your data is stored on this device.{' '}
              <Link to="/login" className="text-primary font-medium">
                Sign in
              </Link>{' '}
              to sync across devices.
            </>
          )}
        </div>

        {/* Status */}
        <section className="grid gap-4 sm:grid-cols-4">
          <StatusCard
            label="Day of cycle"
            value={forecast.dayOfCycle ? `${forecast.dayOfCycle}` : '—'}
          />
          <StatusCard label="Next period" value={forecast.nextPeriod ?? '—'} />
          <StatusCard label="Ovulation" value={forecast.ovulation ?? '—'} />
          <StatusCard
            label="Fertile window"
            value={forecast.fertileStart ? `${forecast.fertileStart} →` : '—'}
            sub={forecast.fertileEnd ?? undefined}
          />
        </section>

        <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_320px]">
          {/* Left: calendar + history */}
          <div className="space-y-8">
            <Calendar
              periodStartDates={periodStartDates}
              periodLength={forecast.periodLength}
              forecast={forecast}
            />

            <section>
              <h2 className="text-text-primary text-lg font-semibold">Cycle history</h2>
              {cycles.length === 0 ? (
                <p className="text-text-muted mt-2 text-sm">No periods logged yet.</p>
              ) : (
                <ul className="divide-border border-border bg-surface-1 mt-3 divide-y rounded-xl border">
                  {cycles.map((c) => (
                    <li key={c.id} className="flex items-center justify-between px-4 py-3">
                      <div className="text-sm">
                        <p className="text-text-primary font-medium">{c.startDate}</p>
                        <p className="text-text-muted">
                          {c.durationDays} days · flow {c.flow}/4
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          removeCycle(c.id)
                          toast.success('Removed')
                        }}
                        className="text-text-muted hover:bg-surface-2 hover:text-error rounded-lg p-2"
                        aria-label="Remove cycle"
                      >
                        <Trash2 size={16} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          {/* Right: log form + symptoms + settings */}
          <div className="space-y-6">
            <LogPeriodForm onAdd={addCycle} defaultDate={today} />

            <section className="border-border bg-surface-1 rounded-2xl border p-4">
              <h2 className="text-text-primary font-semibold">Quick symptom log</h2>
              <SymptomForm onAdd={addSymptom} defaultDate={today} />
              {symptoms.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {symptoms.slice(0, 8).map((s) => (
                    <li key={s.id} className="flex items-center justify-between text-sm">
                      <span className="text-text-secondary">
                        <span className="text-text-muted">{s.date}</span> · {s.type} ({s.severity}
                        /3)
                      </span>
                      <button
                        onClick={() => removeSymptom(s.id)}
                        className="text-text-muted hover:text-error rounded p-1"
                        aria-label="Remove symptom"
                      >
                        <Trash2 size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <SettingsCard settings={settings} onUpdate={updateSettings} />

            <DangerZone onClear={clearAll} />
          </div>
        </div>

        <div className="mt-10">
          <MedicalDisclaimer />
        </div>

        {!loaded && <p className="text-text-muted mt-6 text-sm">Loading your data…</p>}
      </main>
    </div>
  )
}

function StatusCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="border-border bg-surface-1 rounded-2xl border p-4">
      <p className="text-text-muted text-xs font-medium tracking-wide uppercase">{label}</p>
      <p className="text-text-primary mt-1 font-semibold">{value}</p>
      {sub ? <p className="text-text-secondary text-sm">{sub}</p> : null}
    </div>
  )
}

function LogPeriodForm({
  onAdd,
  defaultDate,
}: {
  onAdd: (input: { startDate: string; durationDays: number; flow: 1 | 2 | 3 | 4 }) => Promise<void>
  defaultDate: string
}) {
  const [startDate, setStartDate] = useState(defaultDate)
  const [durationDays, setDurationDays] = useState(5)
  const [flow, setFlow] = useState<1 | 2 | 3 | 4>(3)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    await onAdd({ startDate, durationDays, flow })
    toast.success('Period logged')
  }

  return (
    <form
      onSubmit={onSubmit}
      className="border-border bg-surface-1 space-y-3 rounded-2xl border p-4"
    >
      <h2 className="text-text-primary font-semibold">Log a period</h2>
      <Field label="Start date">
        <input
          type="date"
          required
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className={inputClass}
        />
      </Field>
      <Field label="Duration (days)">
        <input
          type="number"
          min={1}
          max={14}
          value={durationDays}
          onChange={(e) => setDurationDays(Number(e.target.value))}
          className={inputClass}
        />
      </Field>
      <Field label="Flow">
        <select
          value={flow}
          onChange={(e) => setFlow(Number(e.target.value) as 1 | 2 | 3 | 4)}
          className={inputClass}
        >
          <option value={1}>1 — Light</option>
          <option value={2}>2 — Medium</option>
          <option value={3}>3 — Heavy</option>
          <option value={4}>4 — Very heavy</option>
        </select>
      </Field>
      <button type="submit" className={primaryButtonClass}>
        <Plus size={16} /> Add period
      </button>
    </form>
  )
}

function SymptomForm({
  onAdd,
  defaultDate,
}: {
  onAdd: (input: {
    date: string
    type: string
    severity: 1 | 2 | 3
    note?: string
  }) => Promise<void>
  defaultDate: string
}) {
  const [date, setDate] = useState(defaultDate)
  const [type, setType] = useState(SYMPTOM_TYPES[0])
  const [severity, setSeverity] = useState<1 | 2 | 3>(2)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    await onAdd({ date, type, severity })
    toast.success('Symptom logged')
  }

  return (
    <form onSubmit={onSubmit} className="mt-3 space-y-2">
      <input
        type="date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        className={inputClass}
      />
      <div className="flex gap-2">
        <select value={type} onChange={(e) => setType(e.target.value)} className={inputClass}>
          {SYMPTOM_TYPES.map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
        <select
          value={severity}
          onChange={(e) => setSeverity(Number(e.target.value) as 1 | 2 | 3)}
          className={`${inputClass} w-24`}
        >
          <option value={1}>Mild</option>
          <option value={2}>Mod.</option>
          <option value={3}>Severe</option>
        </select>
      </div>
      <button type="submit" className={`${secondaryButtonClass} w-full`}>
        <Plus size={16} /> Add
      </button>
    </form>
  )
}

function SettingsCard({
  settings,
  onUpdate,
}: {
  settings: { avgCycleLength: number; avgPeriodLength: number; lutealPhase: number }
  onUpdate: (patch: {
    avgCycleLength?: number
    avgPeriodLength?: number
    lutealPhase?: number
  }) => Promise<void>
}) {
  return (
    <section className="border-border bg-surface-1 space-y-3 rounded-2xl border p-4">
      <h2 className="text-text-primary font-semibold">Cycle settings</h2>
      <Field label="Average cycle length (days)" hint="Used until you have 2+ logged cycles.">
        <input
          type="number"
          min={15}
          max={60}
          value={settings.avgCycleLength}
          onChange={(e) => onUpdate({ avgCycleLength: Number(e.target.value) })}
          className={inputClass}
        />
      </Field>
      <Field label="Average period length (days)">
        <input
          type="number"
          min={1}
          max={14}
          value={settings.avgPeriodLength}
          onChange={(e) => onUpdate({ avgPeriodLength: Number(e.target.value) })}
          className={inputClass}
        />
      </Field>
      <Field label="Luteal phase (days)" hint="Typically ~14. Affects ovulation estimate.">
        <input
          type="number"
          min={7}
          max={20}
          value={settings.lutealPhase}
          onChange={(e) => onUpdate({ lutealPhase: Number(e.target.value) })}
          className={inputClass}
        />
      </Field>
    </section>
  )
}

function DangerZone({ onClear }: { onClear: () => Promise<void> }) {
  const [confirming, setConfirming] = useState(false)
  if (!confirming) {
    return (
      <button
        onClick={() => setConfirming(true)}
        className="border-border bg-surface-1 text-text-muted hover:text-error w-full rounded-2xl border px-4 py-3 text-left text-sm"
      >
        Erase everything on this device
      </button>
    )
  }
  return (
    <div className="border-error/40 bg-error/5 rounded-2xl border p-4">
      <p className="text-text-primary text-sm font-medium">Erase all local data?</p>
      <p className="text-text-secondary mt-1 text-xs">
        This permanently deletes periods, symptoms and settings on this device.
      </p>
      <div className="mt-3 flex gap-2">
        <button
          onClick={async () => {
            await onClear()
            setConfirming(false)
            toast.success('All local data erased')
          }}
          className="bg-error inline-flex flex-1 items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
        >
          Erase
        </button>
        <button onClick={() => setConfirming(false)} className={`${secondaryButtonClass} flex-1`}>
          Cancel
        </button>
      </div>
    </div>
  )
}

function Calendar({
  periodStartDates,
  periodLength,
  forecast,
}: {
  periodStartDates: string[]
  periodLength: number
  forecast: ReturnType<typeof computeForecast>
}) {
  const today = todayIso()
  const [cursor, setCursor] = useState(() => today.slice(0, 7)) // YYYY-MM

  const [year, month] = cursor.split('-').map(Number)
  const firstDay = new Date(Date.UTC(year, month - 1, 1))
  const startWeekday = firstDay.getUTCDay()
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate()

  const cells: (string | null)[] = []
  for (let i = 0; i < startWeekday; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(`${cursor}-${String(d).padStart(2, '0')}`)

  const move = (delta: number) => {
    const d = new Date(Date.UTC(year, month - 1 + delta, 1))
    setCursor(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`)
  }

  return (
    <section>
      <div className="flex items-center justify-between">
        <h2 className="text-text-primary text-lg font-semibold">
          {MONTHS[month - 1]} {year}
        </h2>
        <div className="flex gap-1">
          <button
            onClick={() => move(-1)}
            className="text-text-secondary hover:bg-surface-2 rounded-lg p-2"
            aria-label="Previous month"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => move(1)}
            className="text-text-secondary hover:bg-surface-2 rounded-lg p-2"
            aria-label="Next month"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <div className="text-text-muted mt-3 grid grid-cols-7 gap-1 text-center text-xs font-medium">
        {WEEKDAYS.map((w, i) => (
          <div key={i} className="py-1">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          if (!day) return <div key={i} />
          const phase = classifyDay(day, forecast, periodStartDates, periodLength)
          const isToday = day === today
          return (
            <div
              key={day}
              title={phase !== 'normal' ? phaseLabel(phase) : undefined}
              className={`flex aspect-square items-center justify-center rounded-lg text-sm font-medium ${phaseClass[phase]} ${
                isToday ? 'ring-primary ring-offset-surface-0 ring-2 ring-offset-1' : ''
              }`}
            >
              {Number(day.slice(-2))}
            </div>
          )
        })}
      </div>

      <div className="text-text-muted mt-3 flex flex-wrap gap-3 text-xs">
        <Legend className="bg-flow-heavy" label="Period" />
        <Legend className="bg-flow-light" label="Predicted" />
        <Legend className="bg-fertile" label="Ovulation" />
        <Legend className="bg-fertile/30" label="Fertile" />
      </div>
    </section>
  )
}

function Legend({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block h-3 w-3 rounded ${className}`} /> {label}
    </span>
  )
}
